import { logEvent } from '@/utils/logs';
import { goTo } from '@/utils/router';
import { getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import { browserLocalPersistence, deleteUser, getAuth, GoogleAuthProvider, initializeAuth, OAuthProvider } from 'firebase/auth';
import {
    getFirestore,
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    initializeFirestore,
    persistentLocalCache,
    persistentMultipleTabManager
} from 'firebase/firestore';

let firebaseApp: FirebaseApp;
let dbInstance: ReturnType<typeof getFirestore>;

export function getFirebaseApp() {
    if (!firebaseApp || getApps().length === 0) {
        firebaseApp = initializeApp({
            apiKey: "AIzaSyCrvSqpDwscztak1N5b8e--5fR1MMEbHuI",
            authDomain: "meyo-230113.firebaseapp.com",
            projectId: "meyo-230113",
            storageBucket: "meyo-230113.appspot.com",
            messagingSenderId: "207500226795",
            appId: "1:207500226795:web:c1babc9ca8a9840ee3ceae",
        });
    }
    return firebaseApp;
}

export function getFirestoreInstance(forceNew = false) {
    const app = getFirebaseApp();

    if (!dbInstance || forceNew) {
        try {
            // Reutiliza la instancia existente si ya fue inicializada
            dbInstance = getFirestore(app);
            console.log("🔁 Reutilizando instancia existente de Firestore");
        } catch (e) {
            // Si nunca se inicializó, crea una nueva con opciones personalizadas
            console.warn("⚠️ Firestore no estaba inicializado. Inicializando con opciones...");
            dbInstance = initializeFirestore(app, {
                localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
            });
            logEvent("✅ Firestore inicializado con opciones personalizadas");
        }
    }

    return dbInstance;
}

export function resetFirestoreInstance() {
    console.warn("♻️ Reinicializando Firestore...");
    return getFirestoreInstance(true);
}
export async function safeSignOut(timeoutMs = 3000) {
    try {
        await Promise.race([
            auth.signOut(),
            new Promise((_, reject) =>
                setTimeout(() => reject(new Error("signOut_timeout")), timeoutMs)
            ),
        ]);
        console.log("✅ Sesión cerrada correctamente");
    } catch (err) {
        console.warn("⚠️ Error o timeout al cerrar sesión:", err);
    }
}
// Initialize Firebase App and Firestore
const app = getFirebaseApp();
const db = getFirestoreInstance();

// Auth
const auth = initializeAuth(app, {
    persistence: browserLocalPersistence, // o indexedDBLocalPersistence si prefieres
});

const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');


export async function firebaseSignInWithIdToken(idToken: string, provider: string) {
    const response = await fetch(
        `https://identitytoolkit.googleapis.com/v1/accounts:signInWithIdp?key=AIzaSyAUju-yxE58OgYR2ELiAILVTMX-hQTToHE`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                postBody: `id_token=${idToken}&providerId=${provider}`,
                requestUri: "https://meyo-230113.firebaseapp.com",
                returnIdpCredential: true,
                returnSecureToken: true,
            }),
        },
    );

    return await response.json();
}

export async function createDoc(collectionName, data) {
    const ref = collection(db, collectionName);
    const docRef = await addDoc(ref, data);
    return docRef.id;
}

export async function getAllDocs(collectionName) {
    const ref = collection(db, collectionName);
    const snapshot = await getDocs(ref);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

export async function getDocById(collectionName: string, id: string): Promise<any | null> {
    let triedReset = false;

    while (true) {
        try {
            const ref = doc(getFirestoreInstance(), collectionName, id);
            const snapshot = await getDoc(ref);

            return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
        } catch (error: any) {
            if (
                !triedReset &&
                error?.message?.includes("IndexedDB")
            ) {
                console.warn("🔁 IndexedDB error detected on getDocById, resetting Firestore...");
                triedReset = true;
                resetFirestoreInstance();
                logEvent(`Firestore IndexedDB error: ${error.message}`);
                setKibanaLogCaloo(appStore.get("user")?.firebaseUser, {
                    key: 'caloo_app',
                    data: {
                        action: 'firestore_indexeddb_error',
                        uid: appStore.get("user")?.firebaseUser,
                        platform: 'Caloo',
                        email: appStore.get("user")?.email,
                        collection: collectionName,
                        documentId: id,
                        error: error.message,
                    }
                });
                continue; // reintenta una vez
            }

            handleFatalFirestoreError({
                action: "get_document_error",
                collection: collectionName,
                documentId: id,
                error,
                context: { id, collectionName },
            });

            return null;
        }
    }
}

export async function updateDocById(collectionName, id, data, merge = true, retries = 3, delay = 500): Promise<boolean> {
    let ref = doc(db, collectionName, id);

    for (let attempt = 1; attempt <= retries; attempt++) {
        try {
            if (merge) {
                await setDoc(ref, data, { merge: true });
            } else {
                await updateDoc(ref, data);
            }
            console.log(`✅ Updated document in ${collectionName}/${id} (attempt ${attempt})`);
            return true;
        } catch (error: any) {
            console.warn(`⚠️ Error updating document in ${collectionName}/${id} (attempt ${attempt}):`, error?.message);

            if (
                error?.message?.includes('IndexedDB') ||
                error?.message?.includes('Connection to Indexed Database server lost')
            ) {
                console.warn("🔁 Detected IndexedDB corruption. Resetting Firestore instance.");
                resetFirestoreInstance();
                ref = doc(getFirestoreInstance(), collectionName, id); // actualizar ref
                logEvent(`Firestore IndexedDB error: ${error.message}`);
                setKibanaLogCaloo(appStore.get("user")?.firebaseUser, {
                    key: 'caloo_app',
                    data: {
                        action: 'firestore_indexeddb_error',
                        uid: appStore.get("user")?.firebaseUser,
                        platform: 'Caloo',
                        email: appStore.get("user")?.email,
                        collection: collectionName,
                        documentId: id,
                        error: error.message,
                    }
                });
                continue; // reintentar en siguiente bucle
            }

            if (attempt === retries) {
                handleFatalFirestoreError({
                    action: "update_document_error",
                    collection: collectionName,
                    documentId: id,
                    error,
                });
                return false;
            }

            await new Promise((res) => setTimeout(res, delay * attempt));
        }
    }

    return false;
}
export async function deleteDocById(collectionName, id) {
    const ref = doc(db, collectionName, id);
    await deleteDoc(ref);
}

export async function getDocsByField(collectionName, field, operator, value) {
    const ref = collection(db, collectionName);
    const q = query(ref, where(field, operator, value));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

export async function getDocByPath(path: string) {
    const ref = doc(db, path);
    const snapshot = await getDoc(ref);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}
/**
 * Save data to Firestore at the given path
 */
export async function saveToFirestore(path: string, data: any, retries = 3, delay = 500): Promise<boolean> {
    const cleanedData = cleanObject(data);
    const [collection, documentId] = path.split("/");

    let triedReset = false;

    for (let attempt = 1; attempt <= retries; attempt++) {
        try {
            const ref = doc(getFirestoreInstance(), path);
            await setDoc(ref, cleanedData, { merge: true });
            console.log(`✅ Saved data to ${path} (attempt ${attempt})`);
            return true;
        } catch (error: any) {
            console.warn(`⚠️ Error saving to ${path} (attempt ${attempt}):`, error);

            const isIndexedDbError = error?.message?.includes("IndexedDB");

            if (!triedReset && isIndexedDbError) {
                console.warn("🔁 IndexedDB error detected on save, resetting Firestore...");
                triedReset = true;
                resetFirestoreInstance();
                logEvent(`Firestore IndexedDB error: ${error.message}`);
                setKibanaLogCaloo(appStore.get("user")?.firebaseUser, {
                    key: 'caloo_app',
                    data: {
                        action: 'firestore_indexeddb_error',
                        uid: appStore.get("user")?.firebaseUser,
                        platform: 'Caloo',
                        email: appStore.get("user")?.email,
                        collection,
                        documentId,
                        error: error.message,
                    }
                });
                continue; // reintenta tras reset
            }

            if (attempt === retries) {
                handleFatalFirestoreError({
                    action: "save_document_error",
                    collection,
                    documentId,
                    error,
                    context: { path },
                });
                return false;
            }

            await new Promise((res) => setTimeout(res, delay * attempt));
        }
    }

    return false;
}

function cleanObject(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(cleanObject);
    } else if (obj !== null && typeof obj === 'object') {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value !== undefined) {
                acc[key] = cleanObject(value);
            }
            return acc;
        }, {} as any);
    } else {
        return obj;
    }
}
/**
 * Load data from Firestore at the given path
 */
export async function loadFromFirestore(path: string, retries = 3, delay = 500): Promise<{ data: any | null; error: boolean }> {
    let triedReset = false;

    for (let attempt = 1; attempt <= retries; attempt++) {
        try {
            const ref = doc(getFirestoreInstance(), path);
            const snap = await getDoc(ref);

            if (snap.exists()) return { data: snap.data(), error: false };
            return { data: null, error: false };
        } catch (error: any) {
            console.warn(`⚠️ Error loading ${path} (attempt ${attempt}):`, error);

            if (!triedReset && error?.message?.includes("IndexedDB")) {
                console.warn("🔁 Resetting Firestore due to IndexedDB error on load");
                resetFirestoreInstance();
                triedReset = true;
                continue;
            }

            if (attempt === retries) {
                handleFatalFirestoreError({
                    action: "load_document_error",
                    collection: path.split("/")[0],
                    documentId: path.split("/")[1],
                    error,
                    context: { path },
                });

                return { data: null, error: true };
            }

            await new Promise(res => setTimeout(res, delay * attempt));
        }
    }

    return { data: null, error: true };
}
export {
    app,
    auth,
    googleProvider,
    appleProvider,
    db,
};


export function handleFatalFirestoreError(params: {
    action: string;
    collection: string;
    documentId?: string;
    error: any;
    context?: Record<string, any>;
}) {
    const { action, collection, documentId, error, context = {} } = params;

    const user = appStore.get("user");

    console.error(`❌ [FIRESTORE ERROR] ${action} failed on ${collection}/${documentId || "?"}:`, error);

    logEvent(`Firestore fatal error on ${collection}/${documentId || "?"}: ${error.message}`);

    setKibanaLogCaloo(user?.firebaseUser, {
        key: "caloo_app",
        data: {
            action,
            uid: user?.firebaseUser,
            platform: "Caloo",
            email: user?.email,
            collection,
            documentId: documentId || null,
            error: error.message,
            ...context,
        },
    });

    trackEvent("firestore_fatal_error", {
        action,
        collection,
        documentId: documentId || "unknown",
        reason: error?.message || "unknown",
        ...context,
    });

    if (navigator.onLine) {
        goTo('/logout', { cause: 'firebase_error' });
    } else {
        goTo('/noConnection');
    }
}


import { Capacitor } from '@capacitor/core';
import { getPlatform } from '@/utils/platform';
import appStore from '@/utils/store';
import { setKibanaLogCaloo } from '../logging';
console.log('✅ Capacitor Firebase Analytics plugin loaded');


/**
 * Trackea una vista de pantalla en GA4 con un nombre personalizado
 */
export async function trackScreen(screenName: string, screenClass?: string) {

}

export async function trackEvent(eventName: string, params?: Record<string, any>) {
    console.log(`📊 Tracking event: ${eventName}`, params || {});
}

export async function deleteAccount(): Promise<void> {
    try {
        const auth = getAuth();
        const db = getFirestore();
        const user = auth.currentUser;

        if (!user) {
            throw new Error("No user is currently logged in.");
        }
        // Antes de eliminar la cuenta de autenticación, eliminamos sus datos en Firestore
        await deleteDoc(doc(db, "users", user.uid));

        // Elimina la cuenta de autenticación
        await deleteUser(user);

        console.log("✅ Cuenta eliminada correctamente.");
    } catch (error: any) {
        console.error("❌ Error al eliminar la cuenta:", error);
        throw error;
    }
}